/**
 * PR-26 — Predictive Alert Engine.
 * Generates rule-based tactical alerts (goal probability spikes, pressure
 * changes, possible penalties, tactical changes). Frontend-only with
 * localStorage history and a lightweight pub/sub via window CustomEvent.
 */
import { trackEvent } from "@/lib/audit";

export type PredictiveAlertKind =
  | "pressure_spike"
  | "goal_probability_high"
  | "dangerous_attack"
  | "shot_on_goal_high"
  | "possible_penalty"
  | "tactical_change";

export type PredictiveAlertChannel = "in_app" | "push" | "vibration" | "sound";

export interface PredictiveAlert {
  alert_id: string;
  kind: PredictiveAlertKind;
  title: string;
  body: string;
  confidence: number;
  channels: PredictiveAlertChannel[];
  created_at: number;
  acknowledged: boolean;
}

const KEY = "vs:pr26:alerts";
const EVT = "ss2026:predictive-alert";

function read(): PredictiveAlert[] {
  if (typeof window === "undefined") return [];
  try { return JSON.parse(window.localStorage.getItem(KEY) ?? "[]"); } catch { return []; }
}
function write(v: PredictiveAlert[]): void {
  if (typeof window !== "undefined") window.localStorage.setItem(KEY, JSON.stringify(v.slice(-200)));
}

export function listAlerts(): PredictiveAlert[] {
  return read().sort((a, b) => b.created_at - a.created_at);
}

export interface EmitAlertInput {
  kind: PredictiveAlertKind;
  title: string;
  body: string;
  confidence?: number;
  channels?: PredictiveAlertChannel[];
}

export function emitAlert(input: EmitAlertInput): PredictiveAlert {
  const a: PredictiveAlert = {
    alert_id: `alt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    kind: input.kind,
    title: input.title,
    body: input.body,
    confidence: Math.min(1, Math.max(0, input.confidence ?? 0.7)),
    channels: input.channels ?? ["in_app"],
    created_at: Date.now(),
    acknowledged: false,
  };
  const list = read();
  list.push(a);
  write(list);
  void trackEvent("predictive_alert_sent", { kind: a.kind, confidence: a.confidence, channels: a.channels });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<PredictiveAlert>(EVT, { detail: a }));
  }
  return a;
}

export function onPredictiveAlert(listener: (a: PredictiveAlert) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => listener((e as CustomEvent<PredictiveAlert>).detail);
  window.addEventListener(EVT, handler);
  return () => window.removeEventListener(EVT, handler);
}

export function acknowledgeAlert(id: string): void {
  const list = read().map((a) => (a.alert_id === id ? { ...a, acknowledged: true } : a));
  write(list);
}

export function clearAlerts(): void {
  if (typeof window !== "undefined") window.localStorage.removeItem(KEY);
}

export function seedDemoAlerts(): PredictiveAlert[] {
  return [
    emitAlert({ kind: "pressure_spike", title: "Presión +40%", body: "La presión de Argentina subió 40% en el último minuto.", confidence: 0.82, channels: ["in_app", "vibration"] }),
    emitAlert({ kind: "goal_probability_high", title: "Probabilidad de gol 92%", body: "Jugada de alto peligro en el área.", confidence: 0.92, channels: ["in_app", "push", "vibration", "sound"] }),
    emitAlert({ kind: "possible_penalty", title: "Posible penal", body: "Contacto detectado en el área grande.", confidence: 0.71, channels: ["in_app", "vibration"] }),
    emitAlert({ kind: "tactical_change", title: "Cambio táctico", body: "Argentina pasa a 3-4-3 con presión alta.", confidence: 0.66, channels: ["in_app"] }),
  ];
}